import { motion, useReducedMotion } from "motion/react";
import { ArrowLeft, Brain, Loader2, Sparkles } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { Reveal, SectionCard } from "../../components/Section";
import { Ziggy } from "../../components/Ziggy";
import { Roadmap3DFromNavigation } from "../../components/sc/roadmap3d";
import { RoadmapBooksHero } from "../../components/sc/roadmap/RoadmapBooksHero";
import { RoadmapBuildingBar } from "../../components/sc/roadmap/RoadmapBuildingBar";
import { StepCelebrationOverlay } from "../../components/sc/roadmap/StepCelebrationOverlay";
import { EASE_OUT } from "../../lib/motion";
import { isReadForPleasurePlan } from "../../lib/learning/copy";
import {
  useCourseNavigation,
  useGenerateRoadmap,
  useRoadmapGenerationState,
} from "../../lib/learning/hooks";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";
import type { PortalStrings } from "../../lib/i18n/usePortalLocale";
import type { CourseLeaf, Pathway } from "../../lib/learning/types";
import type { ZiggyAge } from "../../lib/ziggy";

/**
 * Dedicated plan roadmap page (SC-20 / SC-20b). Visual timeline, books hero,
 * step qualification, and celebration overlay.
 */
export function PlanRoadmapPage({
  pathway,
  ziggyAge = "grown",
  onBack,
  onQualifyStep,
  celebrateLeafId = null,
  onCelebrateDone,
  busyLeafId = null,
  qualifyFailed = false,
}: {
  pathway: Pathway;
  ziggyAge?: ZiggyAge;
  onBack: () => void;
  onQualifyStep: (leaf: CourseLeaf) => void;
  celebrateLeafId?: string | null;
  onCelebrateDone?: () => void;
  /** Step whose qualify quiz is being generated — drives the pin spinner. */
  busyLeafId?: string | null;
  qualifyFailed?: boolean;
}) {
  const reduce = useReducedMotion();
  const { strings: s } = usePortalLocale();
  const navQuery = useCourseNavigation(pathway.id, pathway.onboarding_complete);
  const generateMut = useGenerateRoadmap();
  // Generation may have been kicked off by the intake wizard, which has since
  // unmounted — track it globally so this page shows progress, not a CTA, and
  // so a background failure surfaces here instead of vanishing.
  const roadmapGen = useRoadmapGenerationState(pathway.id);
  const generating = generateMut.isPending || roadmapGen.generating;
  const generationFailed = generateMut.isError || roadmapGen.failed;
  const data = navQuery.data;

  if (isReadForPleasurePlan(pathway.purpose)) {
    return (
      <div className="space-y-4 lg:mx-auto lg:max-w-2xl">
        <PageHeader pathway={pathway} onBack={onBack} strings={s} />
        <SectionCard className="space-y-3 p-5 text-center">
          <Ziggy age={ziggyAge} pose="reading" className="mx-auto size-20" />
          <p className="font-display text-base font-semibold">{s.readForPleasureTitle}</p>
          <p className="text-muted-foreground text-sm">{s.readForPleasureBody}</p>
          <Button type="button" variant="outline" onClick={onBack}>
            {s.backToLearn}
          </Button>
        </SectionCard>
      </div>
    );
  }

  if (navQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    );
  }

  if (navQuery.isError || !data) {
    return (
      <div className="space-y-4">
        <PageHeader pathway={pathway} onBack={onBack} strings={s} />
        <SectionCard className="space-y-3 p-4 text-center">
          <p className="text-muted-foreground text-sm">{s.roadmapLoadFailed}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => navQuery.refetch()}>
            {s.retry}
          </Button>
        </SectionCard>
      </div>
    );
  }

  if (!data.has_roadmap && pathway.roadmap_source === "ai_generated") {
    return (
      <div className="space-y-4 lg:mx-auto lg:max-w-2xl">
        <PageHeader pathway={pathway} onBack={onBack} strings={s} />
        <SectionCard className="hero-celebrate hero-orbs space-y-4 p-5 text-white">
          <div className="flex items-start gap-3">
            <Ziggy
              age={ziggyAge}
              pose={generating ? "thinking" : "curious"}
              size={64}
              glow={false}
              float={!reduce}
              alt=""
            />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-display text-base font-semibold">
                {generating ? s.roadmapBuildingTitle : s.generateRoadmapTitle}
              </p>
              <p className="text-sm text-white/85">
                {generating ? s.roadmapBuildingBody : s.generateRoadmapBody}
              </p>
            </div>
          </div>
          {generating ? (
            <RoadmapBuildingBar />
          ) : (
            <Button
              type="button"
              variant="secondary"
              className="h-11 w-full rounded-xl font-semibold active:scale-[0.98]"
              onClick={() =>
                generateMut.mutate({ id: pathway.id }, { onSuccess: () => navQuery.refetch() })
              }
            >
              <Sparkles className="mr-1.5 size-4" aria-hidden />
              {s.generateRoadmap}
            </Button>
          )}
          {generationFailed && !generating && (
            <p role="alert" className="text-center text-sm text-white/90">
              {s.studyCoachBusy}
            </p>
          )}
        </SectionCard>
      </div>
    );
  }

  if (!data.has_roadmap) {
    return (
      <div className="space-y-4 lg:mx-auto lg:max-w-2xl">
        <PageHeader pathway={pathway} onBack={onBack} strings={s} />
        <SectionCard className="p-5 text-center">
          <p className="text-muted-foreground text-sm">{s.noRoadmapModules}</p>
        </SectionCard>
      </div>
    );
  }

  const showBooks = pathway.book_source === "ai_pick" && data.recommended_books.length > 0;

  return (
    <div className="relative space-y-5 pb-8 lg:mx-auto lg:max-w-4xl">
      <PageHeader
        pathway={pathway}
        onBack={onBack}
        progress={data.completion_percent}
        strings={s}
      />

      {showBooks && (
        <Reveal>
          <RoadmapBooksHero titles={data.recommended_books} ziggyAge={ziggyAge} />
        </Reveal>
      )}

      {data.pacing_notes && (
        <Reveal index={1}>
          <SectionCard className="border-primary/15 bg-primary/5 px-4 py-3">
            <p className="text-muted-foreground text-xs leading-relaxed">{data.pacing_notes}</p>
          </SectionCard>
        </Reveal>
      )}

      {qualifyFailed && (
        <Reveal index={2}>
          <SectionCard className="border-destructive/30 bg-destructive/5 px-4 py-3">
            <p role="alert" className="text-destructive text-sm">
              {s.studyCoachBusy}
            </p>
          </SectionCard>
        </Reveal>
      )}

      <Reveal index={2}>
        <Roadmap3DFromNavigation
          navigation={data}
          busyLeafId={busyLeafId}
          ziggyAge={ziggyAge}
          onQualifyStep={(leaf) => onQualifyStep(leaf)}
        />
      </Reveal>

      <StepCelebrationOverlay
        open={Boolean(celebrateLeafId)}
        ziggyAge={ziggyAge}
        onDone={onCelebrateDone}
      />
    </div>
  );
}

function PageHeader({
  pathway,
  onBack,
  progress,
  strings: s,
}: {
  pathway: Pathway;
  onBack: () => void;
  progress?: number;
  strings: PortalStrings;
}) {
  const reduce = useReducedMotion();
  const pct = progress ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 active:scale-[0.97]"
          aria-label={s.backToLearn}
          onClick={onBack}
        >
          <ArrowLeft className="size-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
            {s.yourRoadmap}
          </p>
          <h1 className="font-display truncate text-xl font-semibold">{pathway.title}</h1>
        </div>
        <Brain className="text-primary/40 size-8 shrink-0" aria-hidden />
      </div>

      {progress != null && (
        <div className="space-y-1.5 px-1">
          <div className="flex items-center justify-between text-xs font-medium">
            <span className="text-muted-foreground">{s.journeyProgress}</span>
            <span className="tabular-nums">{pct}%</span>
          </div>
          <div className="bg-muted/80 h-2 overflow-hidden rounded-full">
            <motion.div
              className="from-primary to-primary/70 h-full rounded-full bg-gradient-to-r"
              initial={false}
              animate={{ width: `${pct}%` }}
              transition={{ duration: reduce ? 0 : 0.45, ease: EASE_OUT }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
